import { URLS } from 'api/constants';
import { IUser } from 'constants/types';
import { useMutation } from 'hooks/useMutation';
import { useLazyQuery, useQuery } from 'hooks/useQuery';
import { apiGet, apiRequest } from 'lib/api/client';
import { getClientToken } from 'lib/auth/token';
import { HookQueryOptions, queryKey } from './shared';

interface UploadPhotoRequest {
  image: File;
}

interface UpdateUsernameRequest {
  username: string;
}

interface ChangeEmailRequest {
  newEmail: string;
}

interface ChangeEmailSubmitRequest {
  token: string;
}

export const useMeQuery = (
  payload?: { isSafe: boolean } | null,
  options?: HookQueryOptions
) => {
  const isSafe = payload?.isSafe ?? false;
  const hasToken = typeof window !== 'undefined' && Boolean(getClientToken());

  return useQuery(
    queryKey('users.me', isSafe),
    () =>
      apiGet<{ success: boolean; user: IUser }>(
        URLS.ME,
        { isSafe },
        { auth: true }
      ),
    { skip: options?.skip || !hasToken }
  );
};

export const useLazyMeQuery = () =>
  useLazyQuery<{ success: boolean; user: IUser }, { isSafe?: boolean } | void>(
    (payload) =>
      apiGet<{ success: boolean; user: IUser }>(
        URLS.ME,
        { isSafe: payload?.isSafe ?? false },
        { auth: true }
      )
  );

export const useUserByIdQuery = (
  payload: { id: string },
  options?: HookQueryOptions
) =>
  useQuery(
    queryKey('users.byId', payload),
    () =>
      apiGet<{ success: boolean; user: IUser }>(`${URLS.USERS}${payload.id}`),
    { skip: options?.skip || !payload?.id }
  );

export const useChangeEmailMutation = () =>
  useMutation<ChangeEmailRequest, void>(({ newEmail }) =>
    apiRequest<void>(URLS.CHANGE_EMAIL, {
      method: 'POST',
      auth: true,
      body: { newEmail },
    })
  );

export const useChangeEmailSubmitMutation = () =>
  useMutation<ChangeEmailSubmitRequest, void>(({ token }) =>
    apiRequest<void>(URLS.CHANGE_EMAIL_SUBMIT, {
      method: 'POST',
      auth: true,
      body: { token },
    })
  );

export const useUserUploadPhotoMutation = () =>
  useMutation<UploadPhotoRequest, void>(({ image }) => {
    const formData = new FormData();
    formData.append('image', image);
    return apiRequest<void>(URLS.UPLOAD_PHOTO, {
      method: 'POST',
      body: formData,
      auth: true,
    });
  });

export const useUserUpdateUsernameMutation = () =>
  useMutation<UpdateUsernameRequest, void>(({ username }) =>
    apiRequest<void>(URLS.UPDATE_USERNAME, {
      method: 'POST',
      auth: true,
      body: { username },
    })
  );
